import type { ReactNode } from 'react'
import { useEffect, useState } from 'react'
import { fromSuggestion } from '../../ParseCSVModal.js'
import { CheckIcon } from '@heroicons/react/24/outline/index.js'
import { ExclamationTriangleIcon } from '@heroicons/react/24/solid/index.js'
import { MapDateTimeValuesModal } from './MapDateTimeValuesModal.js'
import type { PureMetadata } from '../../../../../../Model/Sample/ISampleMetaData.js'
import { parseDateOrUndefined } from './CSVDateTimeParsing.js'
import type { CSVColumnSelectorReturn, CSVTransform } from '../CSVColumnSelectorReturn.js'
import type { CommonConfig, CSVColumnSelectorValidationState } from '../CSVCommonState.js'
import { CSVMetadataSharedSelect } from '../CSVMetadataSharedSelect.js'
import { isClearToken, readCell } from '../CSVClearSentinel.js'
import { fieldsClearedBy } from '../../../../../../Model/Sample/metadataClearing.js'

/**
 * This file contains a component for date/time selector values from a csv
 */
export type DateTimeEntryConfig = CommonConfig & {
  setter: (p: PureMetadata, value: string) => void
}

type CSVDateTimeSelectorProps = {
  config: DateTimeEntryConfig
  onChange: (config: DateTimeEntryConfig, e: CSVColumnSelectorReturn) => void
  disabled?: boolean
  rawCSVData: Record<string, string>[]
  csvHeaders: string[]
  renderInstead: (instead: ReactNode | undefined) => void
  hasSpanningColumnBefore: boolean
}

export type DateTimeValueMapping = { type: 'DISCARD' } | { type: 'MAP_TO'; value: string }

// Function to check if a string can be parsed as a date
export const isValidDate = (dateString: string, assumeUTC: boolean, assumeLocalTimezone: boolean): boolean => {
  if (!dateString) return false

  return !!parseDateOrUndefined(dateString, { assumeUTC, assumeLocal: assumeLocalTimezone })
}

// Function to format a date string to ISO format
export const formatToISO = (dateString: string, assumeUTC: boolean, assumeLocalTimezone: boolean): string => {
  if (!dateString) return ''
  return parseDateOrUndefined(dateString, { assumeUTC, assumeLocal: assumeLocalTimezone }) ?? dateString
}

export function CSVDateTimeSelector(props: CSVDateTimeSelectorProps) {
  const [invalidValues, setInvalidValues] = useState<Set<string> | undefined>(undefined)
  const [validationState, setValidationState] = useState<CSVColumnSelectorValidationState>('NO_DATA')
  const [mapping, setMapping] = useState<Record<string, DateTimeValueMapping>>({})
  const [selectedOption, setSelectedOption] = useState<string>('')
  const [assumeUTC, setAssumeUTC] = useState(false)
  const [assumeLocalTimezone, setAssumeLocalTimezone] = useState(true)

  useEffect(() => {
    if (props.rawCSVData && props.config.type) {
      const suggestion = fromSuggestion(props.config.type, props.rawCSVData, props.csvHeaders)
      setSelectedOption(suggestion)

      if (suggestion) {
        const newInvalidValues = findInvalidValues(suggestion, mapping)
        setInvalidValues(newInvalidValues)

        const newValidationState = newInvalidValues.size === 0 ? 'VALIDATED' : 'INVALID_VALUES'
        setValidationState(newValidationState)
      }
    }
  }, [props.csvHeaders, props.rawCSVData, props.config.type])

  const reactSelectOptions = [
    {
      value: '',
      label: 'Select Column matching the criteria',
      rawKey: ''
    },
    ...props.csvHeaders.map((d: string) => ({
      value: d,
      label: d,
      rawKey: d
    }))
  ]

  const selectedValue = reactSelectOptions.find((opt) => opt.value === selectedOption) || null

  const findInvalidValues = (option: string, mapping: Record<string, DateTimeValueMapping>) => {
    const unmatchedEntries = new Set<string>()
    props.rawCSVData.forEach((row) => {
      const currentValue = row[option]
      if (currentValue && !isClearToken(currentValue) && !mapping[currentValue] && !isValidDate(currentValue, assumeUTC, assumeLocalTimezone)) {
        unmatchedEntries.add(currentValue)
      }
    })
    return unmatchedEntries
  }

  const handleTransform: CSVTransform = (args) => {
    const cell = readCell(args.row, selectedOption)
    if (cell.kind === 'clear') {
      args.clear(fieldsClearedBy(props.config.type))
      return
    }
    const value = cell.kind === 'value' ? cell.value : undefined
    if (value) {
      if (mapping[value]) {
        switch (mapping[value].type) {
          case 'DISCARD':
            /* noop, skip */ break
          case 'MAP_TO':
            props.config.setter(args.result, mapping[value].value)
            break
        }
      } else {
        /* no mapping present, just try to apply as-is */
        props.config.setter(args.result, formatToISO(value, assumeUTC, assumeLocalTimezone))
      }
    }
  }

  const handleChange = (selected: (typeof reactSelectOptions)[number] | null) => {
    setSelectedOption(selected?.value ?? '')
  }

  const handleMappingChange = (changes: Record<string, DateTimeValueMapping>) => {
    setMapping((prev) => {
      return { ...prev, ...changes }
    })
  }

  useEffect(() => {
    setMapping({})
  }, [selectedOption])

  useEffect(() => {
    if (selectedOption) {
      const newInvalidValues = findInvalidValues(selectedOption, mapping)
      setInvalidValues(newInvalidValues)

      const newValidationState = newInvalidValues.size === 0 ? 'VALIDATED' : 'INVALID_VALUES'
      setValidationState(newValidationState)

      props.onChange(props.config, {
        selectedColumn: selectedOption,
        validationState: newValidationState,
        transform: handleTransform
      })
    } else {
      setInvalidValues(undefined)
      setValidationState('NO_DATA')
      props.onChange(props.config, {
        selectedColumn: undefined,
        validationState: 'NO_DATA',
        transform: handleTransform
      })
    }
  }, [mapping, assumeUTC, assumeLocalTimezone])

  return (
    <>
      <CSVMetadataSharedSelect
        config={props.config}
        disabled={props.disabled}
        selectedValue={selectedValue}
        options={reactSelectOptions}
        onChange={handleChange}
        validationState={validationState}
      />

      {/* Status Icon Button - only shown if a column is selected */}
      {validationState !== 'NO_DATA' && (
        <button
          className={`${
            validationState === 'VALIDATED' ? 'text-green-600 hover:text-green-800' : 'text-yellow-600 hover:text-yellow-800'
          } text-lg`}
          onClick={() => {
            // Open the modal if there are invalid values or if there are any mappings to display
            if ((validationState === 'INVALID_VALUES' && invalidValues && invalidValues.size > 0) || Object.keys(mapping).length > 0) {
              props.renderInstead(
                <MapDateTimeValuesModal
                  invalidValues={invalidValues ?? new Set()}
                  mapping={mapping}
                  handleMappingChange={handleMappingChange}
                  close={() => props.renderInstead(undefined)}
                />
              )
            }
          }}
          title={
            validationState === 'VALIDATED'
              ? 'All values are valid - click to view or edit mappings'
              : `${invalidValues?.size ?? -1} entries cannot be parsed as dates. Click to fix`
          }
        >
          {validationState === 'VALIDATED' ? <CheckIcon className="h-5 w-5" /> : <ExclamationTriangleIcon className="h-5 w-5" />}
        </button>
      )}
      {validationState === 'NO_DATA' && <span></span>}
      {/* Timezone Controls - only shown if a column is selected */}
      {selectedOption && (
        <>
          { /* layout must know if there is a row-spanning element before in the flex grid */ }
          { !props.hasSpanningColumnBefore && <span></span>}
          <div className={`col-span-3 ml-4 mb-2 flex flex-col`}>
            <div className="flex items-center mb-1">
              <input
                type="checkbox"
                className="checkbox checkbox-sm mr-2"
                checked={assumeUTC}
                onChange={(e) => {
                  setAssumeUTC(e.target.checked)
                  if (e.target.checked) {
                    setAssumeLocalTimezone(false)
                  }
                }}
              />
              <label className="text-sm">Assume UTC (for dates without timezone)</label>
            </div>
            <div className="flex items-center">
              <input
                type="checkbox"
                className="checkbox checkbox-sm mr-2"
                checked={assumeLocalTimezone}
                onChange={(e) => {
                  setAssumeLocalTimezone(e.target.checked)
                  if (e.target.checked) {
                    setAssumeUTC(false)
                  }
                }}
              />
              <label className="text-sm">Assume my timezone: {Intl.DateTimeFormat().resolvedOptions().timeZone}</label>
            </div>
          </div>
        </>
      )}
    </>
  )
}
